// Resolve + auto-detect the dashboard label→stage mapping.
//
// The dashboard renders five Loom stages. Each GitLab project declares which
// labels belong to which stage in its `.loom.yml` under `dashboard.stage_mapping`.
// When the file is absent or a stage is left out, we fall back to a default
// mapping that uses Loom's own `workflow::*` scoped-label vocabulary (see
// @loom/shared/labels). When a project has never seen Loom before, the server
// can take its real label list and suggest a mapping by pattern-matching
// against common conventions; the customer can accept the suggestion with
// one click, which writes it back to `.loom.yml`.

import { DASHBOARD_STAGES, type DashboardStage } from "../dashboard/index.js";
import { PROPOSAL_PENDING_LABEL, scopedLabel } from "../labels/index.js";

import type { DashboardConfig, DashboardStageMapping } from "./loom-config.js";

/**
 * Built-in default stage mapping. Uses Loom's own `workflow::*` vocabulary
 * (the labels the Loom workflows create + manage themselves). A project that
 * has never seen Loom can use these directly if it follows the standard.
 */
export const DEFAULT_STAGE_MAPPING: Readonly<Record<DashboardStage, readonly string[]>> = {
  needs_input: [scopedLabel("workflow", "discovery")],
  planned: [scopedLabel("workflow", "ready")],
  in_progress: [scopedLabel("workflow", "in-progress")],
  review: [scopedLabel("workflow", "review")],
  shipped: [scopedLabel("workflow", "done")],
};

/**
 * Built-in default decision-bubbling labels. Loom workflows write
 * `loom::needs-decision` and `loom::proposal-pending`; both surface as
 * decisions inbox rows even before the project owner customises anything.
 */
export const DEFAULT_DECISION_LABELS: readonly string[] = [
  "loom::needs-decision",
  PROPOSAL_PENDING_LABEL,
];

/**
 * A proposal someone declined: closed while it still carries
 * `PROPOSAL_PENDING_LABEL` (Decline closes without accepting). It never
 * shipped, so lead time and shipped counts leave it out.
 */
export const isDeclinedProposal = (
  labels: readonly string[],
  closed: boolean,
): boolean => closed && labels.includes(PROPOSAL_PENDING_LABEL);

export interface ResolvedDashboardMapping {
  readonly stageMapping: Readonly<Record<DashboardStage, readonly string[]>>;
  readonly decisionLabels: readonly string[];
}

/**
 * Merge the project's `.loom.yml` dashboard block (if any) with the defaults.
 * Each stage falls back independently: a project may override only
 * `in_progress` and leave the rest on defaults.
 */
export const resolveDashboardMapping = (
  config: DashboardConfig | undefined,
): ResolvedDashboardMapping => {
  const stageMapping: Record<DashboardStage, readonly string[]> = {
    needs_input: config?.stage_mapping?.needs_input ?? DEFAULT_STAGE_MAPPING.needs_input,
    planned: config?.stage_mapping?.planned ?? DEFAULT_STAGE_MAPPING.planned,
    in_progress: config?.stage_mapping?.in_progress ?? DEFAULT_STAGE_MAPPING.in_progress,
    review: config?.stage_mapping?.review ?? DEFAULT_STAGE_MAPPING.review,
    shipped: config?.stage_mapping?.shipped ?? DEFAULT_STAGE_MAPPING.shipped,
  };
  const decisionLabels = config?.decision_labels ?? DEFAULT_DECISION_LABELS;
  return { stageMapping, decisionLabels };
};

/**
 * Decide a single issue's Loom stage from its current labels + open/closed
 * state. Closed always wins (a closed issue is shipped, regardless of labels).
 * Among open issues, later stages in `DASHBOARD_STAGES` order win — an issue
 * with both an in-progress and a review label is treated as in review.
 *
 * Returns `null` when no configured label matches; the caller decides what
 * to do (the dashboard treats null as `planned` — "ready to pick up" — when
 * the issue is otherwise unblocked).
 */
export const stageFromLabels = (
  mapping: ResolvedDashboardMapping["stageMapping"],
  labels: readonly string[],
  closed: boolean,
): DashboardStage | null => {
  if (closed) return "shipped";
  const labelSet = new Set(labels);
  let match: DashboardStage | null = null;
  for (const stage of DASHBOARD_STAGES) {
    if (stage === "shipped") continue; // shipped = closed; never derived from open issues
    const patterns = mapping[stage];
    for (const pattern of patterns) {
      if (labelMatches(pattern, labelSet)) {
        match = stage;
        break;
      }
    }
  }
  return match;
};

// ---------------------------------------------------------------------------
// Auto-detection — propose a mapping from a project's actual labels
// ---------------------------------------------------------------------------

/**
 * Common scope prefixes we recognise. The order is preference order: a
 * project that has both `workflow::*` and `kanban::*` will get a mapping
 * built from `workflow::*` (Loom's own vocabulary).
 */
const KNOWN_SCOPES = ["workflow", "kanban", "state", "status"] as const;

interface ScopeRecipe {
  readonly needs_input: readonly string[];
  readonly planned: readonly string[];
  readonly in_progress: readonly string[];
  readonly review: readonly string[];
  readonly shipped: readonly string[];
}

/**
 * What value-substrings, under a given scope, count as each stage. Matched
 * case-insensitively against the value half of `<scope>::<value>`. Multiple
 * matches per stage are common — many teams label "in progress" exactly so,
 * but others use "doing" or "wip".
 */
const SCOPE_RECIPES: Readonly<Record<(typeof KNOWN_SCOPES)[number], ScopeRecipe>> = {
  workflow: {
    needs_input: ["discovery", "needs", "refinement", "needs-input"],
    planned: ["ready", "up-next", "todo", "backlog"],
    in_progress: ["in-progress", "in progress", "wip", "doing"],
    review: ["review", "in-review", "qa"],
    shipped: ["done", "shipped", "complete", "closed"],
  },
  kanban: {
    needs_input: ["needs-refinement", "refinement", "discovery"],
    planned: ["up-next", "up next", "backlog", "ready", "todo"],
    in_progress: ["in-progress", "in progress", "wip", "doing"],
    review: ["review", "in-review", "qa", "verify"],
    shipped: ["done", "shipped", "complete"],
  },
  state: {
    needs_input: ["needs-refinement", "refinement", "discovery", "blocked"],
    planned: ["planned", "backlog", "up-next", "ready"],
    in_progress: ["in-progress", "in progress", "doing"],
    review: ["in-review", "review", "qa"],
    shipped: ["done", "shipped", "closed"],
  },
  status: {
    needs_input: ["needs-refinement", "refinement", "blocked"],
    planned: ["planned", "todo", "ready", "up-next"],
    in_progress: ["in-progress", "in progress", "doing", "wip"],
    review: ["in-review", "review"],
    shipped: ["done", "shipped", "closed"],
  },
};

export interface DashboardMappingSuggestion {
  readonly mapping: DashboardStageMapping;
  readonly decisionLabels: readonly string[];
  /** Which scope drove the suggestion. `null` when no scope had any matches. */
  readonly chosenScope: (typeof KNOWN_SCOPES)[number] | null;
  /**
   * How confident the suggestion is. `high` means all five stages had a
   * matching label; `partial` means at least one matched; `none` means we
   * fell back to the built-in workflow:: defaults entirely.
   */
  readonly confidence: "high" | "partial" | "none";
}

/**
 * Suggest a stage mapping from a list of GitLab labels on the project.
 * Pure function over the list — the caller decides whether to write the
 * suggestion back to `.loom.yml`.
 */
export const suggestDashboardMapping = (
  projectLabels: readonly string[],
): DashboardMappingSuggestion => {
  const byScope = new Map<string, string[]>();
  for (const label of projectLabels) {
    const sepIdx = label.indexOf("::");
    if (sepIdx < 0) continue;
    const scope = label.slice(0, sepIdx).toLowerCase();
    const list = byScope.get(scope) ?? [];
    list.push(label);
    byScope.set(scope, list);
  }

  // Try each known scope in preference order. First scope that produces at
  // least one stage match wins.
  for (const scope of KNOWN_SCOPES) {
    const labelsForScope = byScope.get(scope);
    if (!labelsForScope) continue;
    const recipe = SCOPE_RECIPES[scope];
    const mapping = matchRecipe(labelsForScope, recipe);
    const matchedStages = DASHBOARD_STAGES.filter((s) => {
      const v = mapping[s];
      return Array.isArray(v) && v.length > 0;
    });
    if (matchedStages.length === 0) continue;
    const decisionLabels = collectDecisionLabels(projectLabels);
    return {
      mapping,
      decisionLabels,
      chosenScope: scope,
      confidence: matchedStages.length === DASHBOARD_STAGES.length ? "high" : "partial",
    };
  }

  return {
    mapping: {},
    decisionLabels: collectDecisionLabels(projectLabels),
    chosenScope: null,
    confidence: "none",
  };
};

const matchRecipe = (
  labelsInScope: readonly string[],
  recipe: ScopeRecipe,
): DashboardStageMapping => {
  const mapping: DashboardStageMapping = {};
  for (const stage of DASHBOARD_STAGES) {
    const hints = recipe[stage];
    const hits = labelsInScope.filter((label) => {
      const valueLower = label.slice(label.indexOf("::") + 2).toLowerCase();
      return hints.some((hint) => valueLower.includes(hint));
    });
    if (hits.length > 0) mapping[stage] = hits;
  }
  return mapping;
};

/** Decision-label suggestions: anything matching the known Loom marks. */
const collectDecisionLabels = (projectLabels: readonly string[]): readonly string[] => {
  const found = new Set<string>();
  for (const dl of DEFAULT_DECISION_LABELS) found.add(dl);
  for (const label of projectLabels) {
    const lower = label.toLowerCase();
    if (lower.startsWith("loom::needs") || lower.startsWith("loom::proposal")) {
      found.add(label);
    }
    if (lower.startsWith("blocked::")) found.add(label);
  }
  return Array.from(found);
};

// ---------------------------------------------------------------------------
// Label matching — used by `stageFromLabels` above
// ---------------------------------------------------------------------------

/**
 * Exact, case-sensitive label match (no globbing in v1). The pattern is the
 * label name as it appears in GitLab. Extending to wildcards would mean
 * either documenting glob syntax or running these through `minimatch`; both
 * are deferred until a real project asks for them.
 */
const labelMatches = (pattern: string, labels: ReadonlySet<string>): boolean =>
  labels.has(pattern);

// ---------------------------------------------------------------------------
// "Already built": a workflow label that says the work is done or waits for
// its release while the issue is still open.
// ---------------------------------------------------------------------------

/**
 * Label values (the part after `::` on a scoped label under a
 * {@link RELEASED_LABEL_SCOPES} scope, else the whole label)
 * that say the work is built: common conventions, matched case-insensitively
 * with `-`, `_` and runs of spaces read as one space. `SCOPE_RECIPES`'
 * `shipped` hints and the release step teams put before it ("Ready for
 * Release"). One project's own name for it belongs in its `.loom.yml`
 * `dashboard.stage_mapping.shipped`, which {@link releasedLabelOf} reads too.
 */
export const RELEASED_LABEL_HINTS: readonly string[] = [
  "done",
  "shipped",
  "complete",
  "completed",
  "released",
  "deployed",
  "in production",
  "ready for release",
  "ready to release",
  "release ready",
  "ready for deploy",
  "ready to deploy",
  "ready for production",
];

/**
 * Scopes of a scoped label that name the issue's own state. A "done" under
 * another scope (`design::done`, `qa::done`, `review::done`) says one part
 * is finished, not the work.
 */
export const RELEASED_LABEL_SCOPES: readonly string[] = [
  "workflow",
  "status",
  "state",
  "stage",
  "kanban",
  "board",
];

const normaliseLabelPart = (part: string): string =>
  part.toLowerCase().replace(/[-_\s]+/gu, " ").trim();

/** The label's value when it can say the work is built: an unscoped label
 *  whole, a scoped one's part after `::` only under a
 *  {@link RELEASED_LABEL_SCOPES} scope; else null. */
const releaseValueOf = (label: string): string | null => {
  const sep = label.indexOf("::");
  if (sep < 0) return normaliseLabelPart(label);
  if (!RELEASED_LABEL_SCOPES.includes(normaliseLabelPart(label.slice(0, sep)))) return null;
  return normaliseLabelPart(label.slice(sep + 2));
};

/**
 * The first of an issue's labels that says its work is already built, or
 * null: a label the project maps to the `shipped` stage (exact, as
 * `stageFromLabels` matches), else one whose value is a
 * {@link RELEASED_LABEL_HINTS} entry. Loom's own `loom::` labels never count.
 * A closed issue is the caller's own check (it is shipped whatever it says).
 */
export const releasedLabelOf = (
  labels: readonly string[],
  shippedLabels: readonly string[],
): string | null => {
  const shipped = new Set(shippedLabels);
  for (const label of labels) {
    if (label.toLowerCase().startsWith("loom::")) continue;
    if (shipped.has(label)) return label;
    const value = releaseValueOf(label);
    if (value !== null && RELEASED_LABEL_HINTS.includes(value)) return label;
  }
  return null;
};
